import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ChatBubble from 'eCoach/ChatBubble';
import TextField from '@mui/material/TextField';

// styles
import * as Buttons from 'theme/components/Buttons.style';
import * as StyledPromptControls from 'eCoach/_styles/PromptControls.style';
import * as Text from 'theme/components/Text.style';

interface WarningLetterIntegerInputProps {
  sendUserInput: (text: string, type: number) => void;
  inputType: number;
}

export default function WarningLetterIntegerInput({
  sendUserInput,
  inputType,
}: WarningLetterIntegerInputProps): JSX.Element {
  const { t } = useTranslation('pageEcoach');
  const [inputValue, setInputValue] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    // Only allow numbers
    if (value === '' || /^[0-9]+$/.test(value)) {
      setInputValue(value);
      setErrorMessage('');
    }
  };

  const handleSubmit = () => {
    if (inputValue === '') {
      setErrorMessage(t('Please enter a number'));
      return;
    }

    const numValue = Number(inputValue);
    if (Number.isNaN(numValue) || numValue < 0 || !Number.isInteger(numValue)) {
      setErrorMessage(t('Please enter a valid non-negative integer'));
      return;
    }

    sendUserInput(inputValue, inputType);
  };

  return (
    <ChatBubble sentByUser>
      <StyledPromptControls.InputGroup>
        <Text.P2>{t('Please enter a non-negative integer')}</Text.P2>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <TextField
            value={inputValue}
            onChange={handleInputChange}
            placeholder="0"
            fullWidth
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Buttons.SecondaryDark onClick={handleSubmit} small stretch>
          {t('Submit')}
        </Buttons.SecondaryDark>
      </StyledPromptControls.InputGroup>
    </ChatBubble>
  );
}
